'use client'

import { IUserSignUp, TNullable, TToken } from '@ftd/types'
import { pick } from 'lodash-es'
import { NextApiRequest, NextApiResponse } from 'next'
import { Session } from 'next-auth'
import { useSession } from 'next-auth/react'
import React, { createContext } from 'react'

import { log, redirectToSignInPage, signOutUser } from '@/lib'

type TUser = IUserSignUp['user'] | null

type TSessionStatus = 'loading' | 'authenticated' | 'unauthenticated'

export type TAuthProvider = {
  authLogout: (req?: NextApiRequest, res?: NextApiResponse) => void
  isAuthenticated: boolean
  session: TNullable<Session>
  sessionStatus: TSessionStatus
  token: TToken
  user: TUser
}

export const AuthContext = createContext<TAuthProvider | null>(null)

/**
 * Authentication context provider
 * usage:
 *
 * const {
 *  user,
 *  token,
 *  authLogout,
 *  isAuthenticated,
 *  session,
 *  sessionStatus,
 * } = useContext(AuthContext)
 */
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: session, status } = useSession({
    required: true,
    onUnauthenticated() {
      log.debug('AuthProvider:: missing session, redirecting to sign-in page')
      redirectToSignInPage()
    },
  })

  const storedUser = session ? pick(session, ['email', 'basecurrency']) : null

  const authLogout = async () => {
    log.debug('AuthProvider:: authLogout called')
    await signOutUser()
  }

  const value: TAuthProvider = {
    authLogout,
    isAuthenticated: !!session?.accessToken,
    session,
    sessionStatus: status as TSessionStatus,
    token: session?.accessToken || '',
    user: storedUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
